"use client";

import React from "react";
import { CircleAlert as AlertCircle, ArrowRight, Droplets, Flame, Search, CircleCheck as CheckCircle2, Wrench, Activity, PhoneCall } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const services = [{
  id: "emergency-plumbing",
  title: "Emergency Plumbing",
  description: "Immediate 24/7 rapid dispatch for burst pipes, sudden floods, and active plumbing crises across Jonesboro.",
  link: "/emergency-plumbing/",
  badge: "24/7 Priority",
  icon: AlertCircle
}, {
  id: "drain-cleaning",
  title: "Drain Cleaning",
  description: "Thorough blockage removal, hydro jetting, and clearing for stubborn kitchen sinks, showers, and main drains.",
  link: "/drain-cleaning/",
  badge: "Fast Relief",
  icon: Droplets
}, {
  id: "water-heater-repair",
  title: "Water Heater Repair",
  description: "Expert diagnostic and repairs for tankless, gas, and electric water heaters with zero hot water delays.",
  link: "/water-heater-repair/",
  badge: "Same-Day",
  icon: Flame
}, {
  id: "leak-detection",
  title: "Leak Detection",
  description: "Non-invasive electronic acoustic testing to pinpoint hidden pipe leaks under slabs and behind walls.",
  link: "/leak-detection-repair/",
  badge: "Accurate",
  icon: Search
}, {
  id: "toilet-repair",
  title: "Toilet Repair",
  description: "Fixing stubborn clogs, constant running, weak flushes, faulty seals, and brand replacement installations.",
  link: "/toilet-repair/",
  badge: "All Brands",
  icon: CheckCircle2
}, {
  id: "sewer-line-service",
  title: "Sewer Line Service",
  description: "In-depth camera inspections, trenchless repair options, and complete main line backup restoration.",
  link: "/sewer-line-repair/",
  badge: "HD Camera",
  icon: Activity
}, {
  id: "pipe-repair",
  title: "Pipe Repair",
  description: "Durable fixes for frozen lines, pinhole copper leaks, rusted galvanized pipes, and full home repiping.",
  link: "/pipe-repair/",
  badge: "Guaranteed",
  icon: Wrench
}, {
  id: "faucet-repair",
  title: "Faucet Repair",
  description: "Precision repairs and updates for leaky kitchen taps, bathroom fixtures, outdoor bibs, and sinks.",
  link: "/faucet-sink-repair/",
  badge: "Water Saver",
  icon: Droplets
}];
export default function Services() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="coreservices" data-nav="light" className="bg-background text-foreground py-20 lg:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-14 lg:mb-18">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground">
            Jonesboro Plumbing Solutions
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl text-foreground">
            Core Plumbing Services
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            From midnight burst pipes to scheduled replacements, our certified Jonesboro technicians handle every plumbing emergency with upfront pricing.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
          const IconComponent = service.icon;
          return <motion.div key={service.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.15
          }} transition={{
            duration: 0.45,
            delay: shouldReduceMotion ? 0 : index * 0.05,
            ease: "easeOut"
          }} className="h-full">
                <Card className="h-full flex flex-col justify-between border border-border bg-card text-card-foreground shadow-sm hover:shadow-md transition-shadow duration-300">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <div className="flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="outline" className="text-xs font-medium border-border">
                        {service.badge}
                      </Badge>
                    </div>
                    <CardTitle className="font-default text-xl font-bold text-card-foreground">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 pb-4">
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                  <CardFooter className="pt-0">
                    <Button variant="ghost" size="sm" className="w-full justify-between px-3 text-primary hover:text-primary hover:bg-primary/5 font-semibold group" asChild>
                      <Link to={service.link}>
                        <span>Service details</span>
                        <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Emergency Help Banner */}
        <div className="mt-14 rounded-2xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="font-default text-xl sm:text-2xl font-bold text-card-foreground">
              Need urgent plumbing assistance in Jonesboro?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              Our dispatch team is on call 24 hours a day, 7 days a week for rapid response.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-3 w-full sm:w-auto">
            <Button size="lg" className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto" asChild>
              <Link to="/contact/">
                <PhoneCall className="size-4" />
                <span>Request Urgent Service</span>
              </Link>
            </Button>
            <Button variant="outline" size="lg" className="w-full sm:w-auto" asChild>
              <Link to="/services/">
                <span>View All Services</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}